/**
 * 사용 기록 모으기 (공통/보안과개인정보 5-5)
 *
 * 어느 화면을 얼마나 머물렀는지, 무슨 버튼을 눌렀는지, 흐름을 시작하고 끝냈는지, 오류가 났는지를 모아 서버에 보낸다.
 * 일손이 직원만 보는 사용 현황(6단계)에서 헷갈리는 화면을 찾는 데 쓴다.
 *
 * 개인정보가 섞이지 않게:
 * - 화면 이름은 주소에서 번호를 :id로 바꾼다(/workers/3f2a... → /workers/:id)
 * - 버튼은 화면 코드에 적어 둔 이름(data-usage="저장")만 보낸다. 버튼 글자(인력 이름 등)는 보내지 않는다.
 *   인력 이름을 누르는 버튼처럼 사람마다 다른 버튼은 data-usage="인력 이름"처럼 종류만 적는다.
 * - 오류는 오류 이름(server_error 등)만 보낸다.
 * 로그인한 동안만 모으고, 10초마다 또는 창을 닫을 때 한 번에 보낸다.
 */

type Kind = '화면 열림' | '버튼 누름' | '흐름 시작' | '흐름 완료' | '오류' | '문의하기 누름';

interface UsageEvent {
  screen: string;
  event: Kind;
  target?: string;
  duration?: number;
  error_code?: string;
}

const queue: UsageEvent[] = [];
let on = false;
let screen = '/';
let openedAt = Date.now();
let timer: number | undefined;

/** 주소 속 번호(긴 번호, 숫자)를 :id로 바꿔 사람을 가리키지 않게 한다. */
export function screenName(path: string): string {
  return (path.split('?')[0] ?? '/')
    .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, ':id')
    .replace(/\/\d+(?=\/|$)/g, '/:id')
    .toLowerCase()
    .slice(0, 80);
}

function push(e: UsageEvent): void {
  if (!on) return;
  queue.push(e);
  if (queue.length >= 20) flush();
}

function flush(): void {
  if (queue.length === 0) return;
  const events = queue.splice(0, queue.length);
  // 창을 닫는 순간에도 가도록 keepalive. 실패해도 화면 일에는 아무 영향이 없게 조용히 버린다.
  void fetch('/api/usage', {
    method: 'POST',
    credentials: 'same-origin',
    keepalive: true,
    headers: { 'content-type': 'application/json', 'x-ilsoni': '1' },
    body: JSON.stringify({ events })
  }).catch(() => undefined);
}

/** 지금 화면에서 머문 시간을 남기고 다음 화면으로 넘어간다. */
function leaveScreen(): void {
  push({ screen, event: '화면 열림', duration: Math.max(0, Date.now() - openedAt) });
}

function onClick(ev: MouseEvent): void {
  const el = (ev.target as Element | null)?.closest?.('[data-usage]');
  const name = el?.getAttribute('data-usage');
  if (!name) return;
  push({ screen, event: name === '문의하기' ? '문의하기 누름' : '버튼 누름', target: name });
}

function onHide(): void {
  if (document.visibilityState !== 'hidden') return;
  leaveScreen();
  openedAt = Date.now();
  flush();
}

/** 로그인하면 켜고, 로그아웃하면 끈다. */
export function setUsageOn(next: boolean, path: string): void {
  if (next === on) return;
  if (!next) {
    leaveScreen();
    flush();
  }
  on = next;
  screen = screenName(path);
  openedAt = Date.now();
  if (on) {
    window.addEventListener('click', onClick, true);
    document.addEventListener('visibilitychange', onHide);
    timer = window.setInterval(flush, 10_000);
  } else {
    window.removeEventListener('click', onClick, true);
    document.removeEventListener('visibilitychange', onHide);
    window.clearInterval(timer);
  }
}

/** 화면이 바뀔 때 부른다. */
export function usageScreen(path: string): void {
  const next = screenName(path);
  if (!on || next === screen) return;
  leaveScreen();
  screen = next;
  openedAt = Date.now();
}

/** 가입, 배차처럼 여러 단계 흐름의 시작과 끝(전환율을 보려고). name은 흐름 이름(예: "가입"). */
export function usageFlow(name: string, step: 'start' | 'done'): void {
  push({ screen, event: step === 'start' ? '흐름 시작' : '흐름 완료', target: name });
}

/** 서버가 돌려준 오류 이름. 오류 글은 보내지 않는다. */
export function usageError(code: string): void {
  push({ screen, event: '오류', error_code: code.toLowerCase().replace(/[^a-z0-9_]/g, '_').slice(0, 40) });
}
